"use client";

import * as React from "react";
import Image from "next/image";
import { isSystemAvatarUrl } from "@/lib/avatars/system-avatars";
import { cn } from "@/lib/utils";

const SUPPORTED_FORMATS = ["webp", "avif", "png", "jpg", "jpeg", "svg"];

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

function isValidSrc(src: string | null | undefined): boolean {
  if (!src || !src.trim()) return false;
  if (src.startsWith("data:")) return false;
  try {
    const ext = src.split(".").pop()?.toLowerCase() ?? "";
    if (src.startsWith("/")) return true;
    return SUPPORTED_FORMATS.includes(ext) || src.startsWith("http");
  } catch {
    return false;
  }
}

function stringToHue(str: string): number {
  return str.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
}

function blockImageSave(e: React.SyntheticEvent) {
  e.preventDefault();
}

export type UserAvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const SIZE_MAP: Record<UserAvatarSize, { px: number; cls: string; text: string }> = {
  xs: { px: 20, cls: "size-5", text: "text-[8px]" },
  sm: { px: 28, cls: "size-7", text: "text-[10px]" },
  md: { px: 32, cls: "size-8", text: "text-xs" },
  lg: { px: 40, cls: "size-10", text: "text-sm" },
  xl: { px: 56, cls: "size-14", text: "text-base" },
  "2xl": { px: 112, cls: "size-28", text: "text-xl" },
};

interface UserAvatarProps {
  src?: string | null;
  name?: string | null;
  size?: UserAvatarSize;
  className?: string;
  shape?: "square" | "circle";
  priority?: boolean;
  objectPosition?: string;
  variant?: "default" | "hero";
  /** Block right-click save and drag on preset avatars (default true). */
  protectFromSave?: boolean;
}

export const UserAvatar = React.memo(function UserAvatar({
  src,
  name,
  size = "md",
  className,
  shape = "square",
  priority = false,
  objectPosition,
  variant = "default",
  protectFromSave = true,
}: UserAvatarProps) {
  const isHero = variant === "hero";
  const { px, cls, text } = SIZE_MAP[size];
  const [errored, setErrored] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const validSrc = !errored && isValidSrc(src) ? src! : null;
  const usePresetLayer = Boolean(validSrc && protectFromSave && isSystemAvatarUrl(validSrc));
  const resolvedObjectPosition =
    objectPosition ?? (isHero ? "50% 18%" : "center");

  React.useEffect(() => {
    setErrored(false);
    setLoaded(false);
    if (!validSrc) return;

    const img = new window.Image();
    img.decoding = "async";
    img.onload = () => setLoaded(true);
    img.onerror = () => setErrored(true);
    img.src = validSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [validSrc]);

  const initials = name ? getInitials(name) : "?";
  const hue = name ? stringToHue(name) : 200;

  const radiusCls =
    isHero ? "rounded-3xl" : shape === "circle" ? "rounded-full" : "rounded-lg";

  const saveGuardProps = protectFromSave
    ? {
        onContextMenu: blockImageSave,
        onDragStart: blockImageSave,
      }
    : undefined;

  return (
    <span
      {...saveGuardProps}
      className={cn(
        "relative select-none overflow-hidden [-webkit-user-drag:none]",
        isHero
          ? "mx-auto block aspect-[4/5] w-full max-w-[min(100%,380px)] shadow-[0_32px_80px_-28px_rgba(0,0,0,0.75)] ring-1 ring-white/10"
          : cn("inline-flex shrink-0", cls),
        radiusCls,
        className,
      )}
      aria-label={name ?? "User avatar"}
    >
      {validSrc && !loaded && !errored && (
        <span
          className={cn("absolute inset-0 animate-pulse bg-muted", radiusCls)}
          aria-hidden
        />
      )}

      {validSrc && usePresetLayer ? (
        <span
          role="presentation"
          aria-hidden
          className={cn(
            "absolute inset-0 bg-cover bg-center transition-opacity duration-200",
            loaded ? "opacity-100" : "opacity-0",
          )}
          style={{
            backgroundImage: `url("${validSrc}")`,
            backgroundPosition: resolvedObjectPosition,
          }}
        />
      ) : null}

      {validSrc && !usePresetLayer ? (
        <Image
          key={validSrc}
          src={validSrc}
          alt=""
          fill
          sizes={
            isHero
              ? "(max-width: 640px) 100vw, 380px"
              : `${px * 2}px`
          }
          className={cn(
            "object-cover transition-opacity duration-200 pointer-events-none",
            !objectPosition && (isHero ? "object-[50%_18%]" : "object-center"),
            loaded ? "opacity-100" : "opacity-0",
          )}
          style={objectPosition ? { objectPosition } : undefined}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          priority={priority}
          draggable={false}
        />
      ) : null}

      {isHero && validSrc && !errored ? (
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-[42%] bg-gradient-to-t from-black/55 via-black/20 to-transparent"
          aria-hidden
        />
      ) : null}

      {(!validSrc || errored) && (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center font-semibold leading-none",
            isHero ? "text-3xl" : text,
          )}
          style={{
            background: `hsl(${hue} 55% 25%)`,
            color: `hsl(${hue} 80% 85%)`,
          }}
          aria-hidden
        >
          {initials}
        </span>
      )}
    </span>
  );
});
